import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Container,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Body from '../components/Body';
import Image from '../components/Image';
import LoginDialog from '../forms/LoginDialog';
import { useAuth } from '../data/auth/token';
import HideUserByTokenMutation from '../data/mutations/HideUserByTokenMutation';
import DeleteUserByTokenMutation from '../data/mutations/DeleteUserByTokenMutation';

type Step = 'warning' | 'survey' | 'deleted' | 'kept';
type PendingAction = 'keep' | 'delete';

const REASONS = [
  'met_someone',
  'not_dating',
  'not_for_matchmaking',
  'prefer_self_service',
  'other',
] as const;

export default function DeleteAccount() {
  const { t, i18n } = useTranslation();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  // The sunset token is a userId or base64-encoded email — a logged-in
  // user's own id works as a token, so fall back to it when the URL has none.
  const token = searchParams.get('token') || auth?.user?._id || '';
  const [step, setStep] = useState<Step>('warning');
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState<string>('');
  const [freeText, setFreeText] = useState<string>('');
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);

  const [hideUser] = useMutation(HideUserByTokenMutation);
  const [deleteUser] = useMutation(DeleteUserByTokenMutation);

  const performKeepInNetwork = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await hideUser({ variables: { token: actionToken, language: i18n.language } });
      setStep('kept');
    } finally {
      setSubmitting(false);
    }
  };

  const performDelete = async (actionToken: string) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const feedback = (reason || freeText)
        ? {
          reason: reason || undefined,
          motivation: freeText || undefined,
          extra: ['sunset_exit_survey'],
        }
        : null;
      await deleteUser({ variables: { token: actionToken, feedback, language: i18n.language } });
      setStep('deleted');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeepInNetwork = () => {
    if (!token) {
      // No token in the URL and not logged in — log in to act on own profile.
      setPendingAction('keep');
      return;
    }
    performKeepInNetwork(token);
  };

  const handleDelete = () => {
    if (!token) {
      setPendingAction('delete');
      return;
    }
    performDelete(token);
  };

  return (
    <>
      <Helmet>
        <title>{t('sunset.delete.warning_title')} - Relate</title>
      </Helmet>
      <Body>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Image src="/logo.png" sx={{ width: 65 }} />
        </Box>
        <Container maxWidth="sm" sx={{ py: 8 }}>
          <Box sx={{ color: 'white', textAlign: 'center' }}>

            {step === 'warning' && (
              <>
                <Typography variant="h2" gutterBottom>
                  {t('sunset.delete.warning_title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, opacity: 0.9, whiteSpace: 'pre-line' }}>
                  {t('sunset.delete.warning_body')}
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleKeepInNetwork}
                  disabled={submitting}
                  size="large"
                  sx={{ minWidth: 260, minHeight: 48, mb: 3 }}
                >
                  {t('sunset.delete.keep_cta')}
                </Button>
                <Box>
                  <Link
                    component="button"
                    onClick={() => setStep('survey')}
                    disabled={submitting}
                    sx={{ opacity: 0.6, fontSize: '0.95rem' }}
                  >
                    {t('sunset.delete.proceed_link')}
                  </Link>
                </Box>
              </>
            )}

            {step === 'survey' && (
              <>
                <Typography variant="h2" gutterBottom>
                  {t('sunset.delete.survey_title')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 4, opacity: 0.8 }}>
                  {t('sunset.delete.survey_intro')}
                </Typography>

                <Box sx={{ textAlign: 'left', mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('sunset.delete.reason_label')}
                  </Typography>
                  <RadioGroup
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  >
                    {REASONS.map((key) => (
                      <FormControlLabel
                        key={key}
                        value={key}
                        control={<Radio sx={{ color: 'rgba(255,255,255,0.6)' }} />}
                        label={t(`sunset.delete.reason_${key}`)}
                        sx={{ color: 'white' }}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                <Box sx={{ textAlign: 'left', mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    {t('sunset.delete.free_text_label')}
                  </Typography>
                  <TextField
                    multiline
                    fullWidth
                    minRows={3}
                    maxRows={6}
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value.slice(0, 500))}
                    inputProps={{ maxLength: 500 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255,255,255,0.25)',
                      },
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={submitting}
                  size="large"
                  sx={{ minWidth: 240, minHeight: 48, mb: 3 }}
                >
                  {t('sunset.delete.delete_cta')}
                </Button>
                <Box>
                  <Link
                    component="button"
                    onClick={() => setStep('warning')}
                    disabled={submitting}
                    sx={{ opacity: 0.7, fontSize: '0.95rem' }}
                  >
                    {t('sunset.delete.back_to_keep_link')}
                  </Link>
                </Box>
              </>
            )}

            {step === 'kept' && (
              <>
                <Typography variant="h2" gutterBottom>
                  {t('sunset.hide.confirmed_title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, opacity: 0.9, whiteSpace: 'pre-line' }}>
                  {t('sunset.hide.confirmed_body')}
                </Typography>
              </>
            )}

            {step === 'deleted' && (
              <>
                <Typography variant="h2" gutterBottom>
                  {t('sunset.delete.deleted_title')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, opacity: 0.9, whiteSpace: 'pre-line' }}>
                  {t('sunset.delete.deleted_body')}
                </Typography>
              </>
            )}

          </Box>
        </Container>
        {pendingAction && (
          <LoginDialog
            open
            handleClose={() => setPendingAction(null)}
            onLoggedIn={(user) => {
              const action = pendingAction;
              setPendingAction(null);
              if (!user?._id) return;
              if (action === 'keep') {
                performKeepInNetwork(user._id);
              } else if (action === 'delete') {
                performDelete(user._id);
              }
            }}
          />
        )}
      </Body>
    </>
  );
}
